"use client";

import { useEffect } from "react";

interface Props {
  isOpen: boolean;
  onConfirm: () => void;
  onClose: () => void;
  loading?: boolean;
}

export function CancelConfirmModal({ isOpen, onConfirm, onClose, loading }: Props) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="cancel-modal-root"
      aria-modal="true"
      role="dialog"
      aria-labelledby="cancel-modal-title"
    >
      {/* Backdrop */}
      <div className="cancel-modal-backdrop" onClick={onClose} />

      {/* Sheet */}
      <div className="cancel-modal-sheet">
        {/* Handle */}
        <div className="cancel-modal-handle" />

        {/* Icon */}
        <div className="cancel-modal-icon-wrap">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        </div>

        {/* Content */}
        <h2 id="cancel-modal-title" className="cancel-modal-title">
          Batalkan Pembayaran?
        </h2>
        <p className="cancel-modal-body">
          VA / QR yang sudah dibuat akan dihapus. Kamu perlu memulai proses pembayaran dari awal jika ingin melanjutkan.
        </p>

        {/* Actions */}
        <div className="cancel-modal-actions">
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="cancel-modal-btn-destructive"
          >
            {loading ? "Membatalkan…" : "Ya, Batalkan"}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="cancel-modal-btn-keep"
          >
            Tidak, Kembali
          </button>
        </div>
      </div>

      <style>{`
        .cancel-modal-root {
          position: fixed;
          inset: 0;
          z-index: 9999;
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
        }
        .cancel-modal-backdrop {
          position: absolute;
          inset: 0;
          background: rgba(26, 11, 12, 0.55);
          backdrop-filter: blur(2px);
          animation: cancelFadeIn 0.18s ease;
        }
        .cancel-modal-sheet {
          position: relative;
          background: #FDFAF9;
          border-radius: 24px 24px 0 0;
          padding: 12px 24px 40px;
          animation: cancelSlideUp 0.22s cubic-bezier(0.32, 0.72, 0, 1);
          max-width: 480px;
          width: 100%;
          margin: 0 auto;
        }
        .cancel-modal-handle {
          width: 36px;
          height: 4px;
          border-radius: 2px;
          background: #8F1C21;
          margin: 0 auto 24px;
          opacity: 0.35;
        }
        .cancel-modal-icon-wrap {
          width: 56px;
          height: 56px;
          border-radius: 16px;
          background: #FEF2F2;
          color: #8F1C21;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 16px;
        }
        .cancel-modal-title {
          font-size: 18px;
          font-weight: 800;
          color: #1A0B0C;
          text-align: center;
          margin: 0 0 8px;
          line-height: 1.3;
        }
        .cancel-modal-body {
          font-size: 13px;
          color: #6B5B5C;
          text-align: center;
          line-height: 1.6;
          margin: 0 0 28px;
          max-width: 300px;
          margin-left: auto;
          margin-right: auto;
        }
        .cancel-modal-actions {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .cancel-modal-btn-destructive {
          width: 100%;
          padding: 14px;
          border-radius: 14px;
          background: #8F1C21;
          color: #fff;
          font-size: 14px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          transition: background 0.15s, transform 0.1s;
        }
        .cancel-modal-btn-destructive:hover:not(:disabled) {
          background: #500B0E;
        }
        .cancel-modal-btn-destructive:active:not(:disabled) {
          transform: scale(0.98);
        }
        .cancel-modal-btn-destructive:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .cancel-modal-btn-keep {
          width: 100%;
          padding: 13px;
          border-radius: 14px;
          background: transparent;
          color: #6B5B5C;
          font-size: 14px;
          font-weight: 600;
          border: 1.5px solid #E5DDD7;
          cursor: pointer;
          transition: background 0.15s, border-color 0.15s;
        }
        .cancel-modal-btn-keep:hover:not(:disabled) {
          background: #F5EDEC;
          border-color: #C9B4B4;
        }
        .cancel-modal-btn-keep:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        @keyframes cancelFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes cancelSlideUp {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
        @media (prefers-color-scheme: dark) {
          .cancel-modal-sheet {
            background: #1E1010;
          }
          .cancel-modal-title {
            color: #F5EDEC;
          }
          .cancel-modal-body {
            color: #9A8585;
          }
          .cancel-modal-btn-keep {
            color: #9A8585;
            border-color: #3A2020;
          }
          .cancel-modal-btn-keep:hover:not(:disabled) {
            background: #2A1515;
            border-color: #5A3030;
          }
          .cancel-modal-icon-wrap {
            background: #2A1010;
          }
        }
      `}</style>
    </div>
  );
}
